'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { User, HelpCircle, Menu } from 'lucide-react';
import { IMAGE_NAMES, getImageUrl } from '@/config/imageConstants';

interface LoginHeaderProps {
  variant?: 'transparent' | 'fixed';
  isLoggedIn?: boolean;
  userName?: string;
}

export default function LoginHeader({ variant = 'transparent', isLoggedIn = false, userName }: LoginHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  useEffect(() => {
    if (variant === 'transparent') {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [variant]);

  const headerClasses = variant === 'transparent' 
    ? `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-lg' 
          : 'bg-transparent'
      }`
    : 'fixed top-0 left-0 right-0 z-50 bg-white shadow-lg';

  const textClasses = variant === 'transparent' && !isScrolled 
    ? 'text-gray-800' 
    : 'text-gray-800';

  const logoHref = isLoggedIn ? '/dashboard' : '/';

  return (
    <>
      <header className={headerClasses}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ease-in-out ${isScrolled || variant === 'fixed' ? 'h-20' : 'h-[90px]'}`}>
            {/* Logo */}
            <Link href={logoHref} className="flex items-center space-x-3 group">
              <div className="transition-transform duration-300">
                {isScrolled || variant === 'fixed' ? (
                  <Image
                    src={getImageUrl(IMAGE_NAMES.LOGO)}
                    alt="Marathi Vadhu Var Suchak Logo"
                    width={119}
                    height={55}
                    className="h-[55px] w-auto"
                    priority
                    unoptimized
                  />
                ) : (
                  <>
                    <Image
                      src={getImageUrl(IMAGE_NAMES.LOGO_WHITE)}
                      alt="Marathi Vadhu Var Suchak Logo"
                      width={162}
                      height={75}
                      className="h-[75px] w-auto max-[599px]:hidden"
                      priority
                      unoptimized
                    />
                    <Image
                      src={getImageUrl(IMAGE_NAMES.LOGO)}
                      alt="Marathi Vadhu Var Suchak Logo"
                      width={162}
                      height={75}
                      className="h-[75px] w-auto hidden max-[599px]:block max-[599px]:h-[60px]"
                      priority
                      unoptimized
                    />
                  </>
                )}
              </div>
            </Link>

            {/* Right side links */}
            <div className="flex items-center space-x-6">
              {!isLoggedIn ? (
                <>
                  <Link
                    href="/"
                    className={`group flex items-center space-x-2 hover:text-primary-400 transition-all duration-300 ${textClasses}`}
                  >
                    <div className="p-1 rounded-lg bg-white/10 group-hover:bg-primary-400/20 transition-colors">
                      <User size={20} />
                    </div>
                    <span className="font-normal">Home</span>
                  </Link>
                  <Link
                    href="/help-guide"
                    className={`group flex items-center space-x-2 hover:text-primary-400 transition-all duration-300 ${textClasses}`}
                  >
                    <div className="p-1 rounded-lg bg-white/10 group-hover:bg-primary-400/20 transition-colors">
                      <HelpCircle size={20} />
                    </div>
                    <span className="font-normal">Help</span>
                  </Link>
                </>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="group flex items-center space-x-3 text-gray-800 hover:text-primary-600 transition-all duration-300 bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-xl"
                  >
                    <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                      <User size={20} className="text-primary-600" />
                    </div>
                    <span className="font-medium">Hi, {userName}</span>
                    <Menu size={16} className="group-hover:rotate-180 transition-transform duration-300" />
                  </button>

                  {/* User dropdown */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl py-2 z-50 border border-gray-100">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm text-gray-500">Welcome back!</p>
                        <p className="font-semibold text-gray-800">{userName}</p>
                      </div>
                      <Link
                        href="/profile"
                        className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      >
                        <User size={16} />
                        <span>My Profile</span>
                      </Link>
                      <Link
                        href="/profile/edit"
                        className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      >
                        <User size={16} />
                        <span>Edit Profile</span>
                      </Link>
                      <Link
                        href="/account"
                        className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      >
                        <User size={16} />
                        <span>My Account</span>
                      </Link>
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <button className="flex items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left">
                          <User size={16} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
